import { useCallback, useState } from 'react';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { boatApi } from '../services/boatApi';
import { saveBoatChanges, type SaveBoatChangesArgs, type SaveOutcome } from '../services/boatSave';
import { clearPendingChanges, writePendingChanges, type PendingSurface } from '../utils/pending-boat-changes';
import { boatKeys } from './useBoats';
import { BOAT_MONEY_KEYS, stripMoneyKeys } from '../lib/moneyFields';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';

/** The failures spec §3.5 restores after: the session expired (401/419) or the request never arrived. */
function isExpiredOrOffline(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status === 401 || status === 419;
}

/**
 * #1177 — the changes-only save for one boat on one surface. Pending changes are
 * written to localStorage before the request (so an expired session cannot lose
 * them) and removed once it lands, or once the server rejects it (a 422, a stale
 * version, a 500): those must not come back on the next visit (review I5). A
 * conflict keeps them until the dealer's choice is saved or cancelled. The saved
 * boat seeds the detail cache.
 */
export function useBoatSave(boatId: string | null, surface: PendingSurface) {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const canViewCost = useCan('finance.cost.view');

  const save = useCallback(
    async (args: Omit<SaveBoatChangesArgs, 'send' | 'fetchFresh'>): Promise<SaveOutcome> => {
      if (!boatId) return { kind: 'nothing_to_save', lostRows: [] };

      writePendingChanges(surface, boatId, { base: args.base, current: args.current, savedAt: new Date().toISOString() });
      setIsSaving(true);
      try {
        const outcome = await saveBoatChanges({
          ...args,
          // #1408 (D-08) — the last layer for every changes-only boat save (canvas,
          // detail edit, restore, not-imported pull-back): no money key or
          // equipment cost leaves the SPA for a viewer without finance.cost.view.
          send: (payload) => boatApi.updateBoatChanges(boatId, stripMoneyKeys(payload, BOAT_MONEY_KEYS, canViewCost)),
          fetchFresh: () =>
            queryClient.fetchQuery({ queryKey: boatKeys.detail(boatId), queryFn: () => boatApi.getBoat(boatId), staleTime: 0 }),
        });

        if (outcome.kind === 'saved') {
          clearPendingChanges(surface, boatId);
          queryClient.setQueryData(boatKeys.detail(boatId), outcome.boat);
          void queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
        } else if (outcome.kind !== 'conflicts') {
          clearPendingChanges(surface, boatId);
        }

        return outcome;
      } catch (error) {
        if (!isExpiredOrOffline(error)) clearPendingChanges(surface, boatId);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [boatId, surface, queryClient, canViewCost],
  );

  return { save, isSaving };
}
